'use client';

import { useEffect, useRef } from 'react';

interface ChatSuiteWidgetDemoProps {
  widgetToken?: string;
  apiUrl?: string;
}

/**
 * Embeds the ChatSuite AI chatbot widget on the landing page.
 *
 * Loads the widget script from the ChatSuite API and initializes it
 * with the provided token. If no token is provided, uses a demo token
 * that shows the widget UI without a live AI agent.
 */
export function ChatSuiteWidgetDemo({
  widgetToken,
  apiUrl = 'https://api.chatsuite.akson.ch',
}: ChatSuiteWidgetDemoProps) {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const token = widgetToken ?? process.env.NEXT_PUBLIC_CHATSUITE_WIDGET_TOKEN;

    if (!token) return;

    const script = document.createElement('script');
    script.src = `${apiUrl}/widget/chatsuite.js`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-widget-token', token);
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      try {
        script.remove();
        const container = document.getElementById('chatsuite-widget-root');
        if (container) container.remove();
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [widgetToken, apiUrl]);

  return null;
}
